/**
 * Schedule engine — evaluate whether a habit is due on a given date,
 * compute period boundaries, and enumerate due dates in a range.
 */

import {
  addDays,
  compareDates,
  daysBetween,
  getDayOfWeek,
  getFirstDayOfMonth,
  getLastDayOfMonth,
} from "./day-boundary";

import type { CompletionRecord, PeriodBounds, ScheduleConfig, ScheduleContext } from "./types";

/**
 * Check whether a habit is due on a given date.
 *
 * - daily: always due
 * - specific_days: due if the day-of-week matches
 * - x_per_week: always due (evaluated at period end)
 * - x_per_month: always due (evaluated at period end)
 * - every_n_days: due if enough days have passed since last completion
 */
export function isDue(config: ScheduleConfig, date: string, context: ScheduleContext): boolean {
  // Never due before the habit start date
  if (compareDates(date, context.habitStartDate) < 0) {
    return false;
  }

  switch (config.type) {
    case "daily":
      return true;

    case "specific_days":
      return config.days.includes(getDayOfWeek(date));

    case "x_per_week":
      return true;

    case "x_per_month":
      return true;

    case "every_n_days": {
      // Find most recent completion at or before this date
      const lastCompletion = findLastCompletionBefore(context.completions, date);

      if (!lastCompletion) {
        // No completions yet — due if enough days since habit start
        return daysBetween(context.habitStartDate, date) >= 0;
      }

      return daysBetween(lastCompletion.effective_date, date) >= config.interval;
    }
  }
}

/**
 * Get period boundaries for period-based schedules.
 *
 * - x_per_week: returns week boundaries (respects firstDayOfWeek)
 * - x_per_month: returns month boundaries
 * - daily/specific_days/every_n_days: returns null (no period evaluation)
 */
export function getPeriodBounds(
  config: ScheduleConfig,
  date: string,
  firstDayOfWeek: number,
): PeriodBounds | null {
  switch (config.type) {
    case "daily":
    case "specific_days":
    case "every_n_days":
      return null;

    case "x_per_week":
      return getWeekBounds(date, firstDayOfWeek);

    case "x_per_month":
      return {
        start: getFirstDayOfMonth(date),
        end: getLastDayOfMonth(date),
      };
  }
}

/**
 * Enumerate all due dates in a range [start, end] (inclusive).
 */
export function getDueDatesInRange(
  config: ScheduleConfig,
  start: string,
  end: string,
  context: ScheduleContext,
): string[] {
  const result: string[] = [];
  let current = start;

  // Clamp to habit start date
  if (compareDates(current, context.habitStartDate) < 0) {
    current = context.habitStartDate;
  }

  while (compareDates(current, end) <= 0) {
    if (isDue(config, current, context)) {
      result.push(current);
    }
    current = addDays(current, 1);
  }

  return result;
}

/**
 * Get the start and end of the calendar week containing `date`,
 * where weeks start on `firstDayOfWeek` (0=Sun..6=Sat).
 */
export function getWeekBounds(date: string, firstDayOfWeek: number): PeriodBounds {
  const dow = getDayOfWeek(date);
  const daysSinceStart = (dow - firstDayOfWeek + 7) % 7;
  const weekStart = addDays(date, -daysSinceStart);
  const weekEnd = addDays(weekStart, 6);
  return { start: weekStart, end: weekEnd };
}

/**
 * Find the most recent completion record strictly before a given date.
 * Does not include completions on the date itself — for every_n_days,
 * the due check asks "should I do it today?" which depends on prior completions.
 */
function findLastCompletionBefore(
  completions: CompletionRecord[],
  date: string,
): CompletionRecord | undefined {
  let latest: CompletionRecord | undefined;

  for (const c of completions) {
    if (c.type === "skip") continue; // skips don't count as "doing the habit"
    if (compareDates(c.effective_date, date) >= 0) continue; // strictly before
    if (!latest || compareDates(c.effective_date, latest.effective_date) > 0) {
      latest = c;
    }
  }

  return latest;
}
